import React, { useState, useMemo } from 'react';
import { RiLoader5Fill } from 'react-icons/ri'
import cx from "classnames";

import {
  yaxisFormatterNumber,
  yaxisFormatterPercent,
  yaxisFormatter,
  tooltipLabelFormatter,
  tooltipLabelFormatterUnits,
  tooltipFormatter,
  tooltipFormatterNumber,
  tooltipFormatterPercent,
  formatNumber,
  tsToIsoDate,
  CHART_HEIGHT,
  YAXIS_WIDTH,
  COLORS,
  GREEN,
  RED
} from '../helpers'

import {
  LineChart,
  BarChart,
  Line,
  Bar,
  Label,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
  ReferenceLine,
  Area,
  AreaChart,
  ComposedChart,
  Cell,
  PieChart,
  Pie
} from 'recharts';

import ChartWrapper from '../components/ChartWrapper'
import VolumeChart from '../components/VolumeChart'
import FeesChart from '../components/FeesChart'
import GenericChart from '../components/GenericChart'

import {
  useReferralsData,
} from '../dataProvider'

import { useReferralsDataBesc } from '../dataProviderBesc'

const NOW = Math.floor(Date.now() / 1000)

function Referrals(props) {
  const [fromValue, setFromValue] = useState()
  const [toValue, setToValue] = useState()

  const { mode } = props

  const from = fromValue ? +new Date(fromValue) / 1000 : undefined
  const to = toValue ? +new Date(toValue) / 1000 : NOW
  const chainName = props.match?.params?.chainName || 'besc'
  const isBesc = chainName === 'besc'
  const params = { from, to, chainName }

  const [bescReferralsData, bescReferralsLoading] = useReferralsDataBesc({ from, to })
  const [legacyReferralsData, legacyReferralsLoading] = useReferralsData(isBesc ? { disabled: true } : params)

  const referralsData    = isBesc ? bescReferralsData    : legacyReferralsData
  const referralsLoading = isBesc ? bescReferralsLoading : legacyReferralsLoading

  const stats = useMemo(() => {
    if (!referralsData) {
      return null
    }
    const last = referralsData[referralsData.length - 1] || {}
    const prev = referralsData[referralsData.length - 2] || {}

    const totalVolume             = last.volumeCumulative
    const totalVolumeDelta        = totalVolume && prev.volumeCumulative ? totalVolume - prev.volumeCumulative : null
    const totalDiscountUsd        = last.discountUsdCumulative || 0
    const totalDiscountUsdDelta   = totalDiscountUsd && prev.discountUsdCumulative ? totalDiscountUsd - prev.discountUsdCumulative : null
    const totalReferrerRebateUsd  = last.totalRebateUsdCumulative || 0
    const totalReferrerRebateUsdDelta = totalReferrerRebateUsd && prev.totalRebateUsdCumulative ? totalReferrerRebateUsd - prev.totalRebateUsdCumulative : null
    const totalReferrersCount     = last.referrersCountCumulative
    const totalReferrersCountDelta = totalReferrersCount && prev.referrersCountCumulative ? totalReferrersCount - prev.referrersCountCumulative : null
    const totalReferralsCount     = last.referralsCountCumulative
    const totalReferralsCountDelta = totalReferralsCount && prev.referralsCountCumulative ? totalReferralsCount - prev.referralsCountCumulative : null

    return {
      totalVolume,
      totalVolumeDelta,
      totalDiscountUsd,
      totalDiscountUsdDelta,
      totalReferrerRebateUsd,
      totalReferrerRebateUsdDelta,
      totalReferrersCount,
      totalReferrersCountDelta,
      totalReferralsCount,
      totalReferralsCountDelta
    }
  }, [referralsData])

  return (
    <div className="Home">
      <h1>Analytics / Referrals{isBesc ? ' — BESC Hyperchain' : ''}</h1>
      <div className="chart-grid">
        <div className="chart-cell stats">
          {stats ? <>
            <div className="total-stat-label">Referral Volume</div>
            <div className="total-stat-value">
              {formatNumber(stats.totalVolume, {currency: true})}
              {stats.totalVolumeDelta &&
                <span className="total-stat-delta plus" title="Change since previous day">+{formatNumber(stats.totalVolumeDelta, {currency: true, compact: true})}</span>
              }
            </div>
          </> : <RiLoader5Fill size="3em" className="loader" />}
        </div>
        <div className="chart-cell stats">
          {stats ? <>
            <div className="total-stat-label">Traders Rebates</div>
            <div className="total-stat-value">
              {formatNumber(stats.totalDiscountUsd, {currency: true})}
              {stats.totalDiscountUsdDelta &&
                <span className="total-stat-delta plus" title="Change since previous day">+{formatNumber(stats.totalDiscountUsdDelta, {currency: true, compact: true})}</span>
              }
            </div>
          </> : <RiLoader5Fill size="3em" className="loader" />}
        </div>
        <div className="chart-cell stats">
          {stats ? <>
            <div className="total-stat-label">Affiliates Rebates</div>
            <div className="total-stat-value">
              {formatNumber(stats.totalReferrerRebateUsd, {currency: true})}
              {stats.totalReferrerRebateUsdDelta &&
                <span className="total-stat-delta plus" title="Change since previous day">+{formatNumber(stats.totalReferrerRebateUsdDelta, {currency: true, compact: true})}</span>
              }
            </div>
          </> : <RiLoader5Fill size="3em" className="loader" />}
        </div>
        <div className="chart-cell stats">
          {stats ? <>
            <div className="total-stat-label">Affiliates Registered</div>
            <div className="total-stat-value">
              {stats.totalReferrersCount}
              {stats.totalReferrersCountDelta ?
                <span className="total-stat-delta plus" title="Change since previous day">+{stats.totalReferrersCountDelta}</span> : null
              }
            </div>
          </> : <RiLoader5Fill size="3em" className="loader" />}
        </div>
        <div className="chart-cell stats">
          {stats ? <>
            <div className="total-stat-label">Referrals Registrations</div>
            <div className="total-stat-value">
              {stats.totalReferralsCount}
              {stats.totalReferralsCountDelta ?
                <span className="total-stat-delta plus" title="Change since previous day">+{stats.totalReferralsCountDelta}</span> : null
              }
            </div>
          </> : <RiLoader5Fill size="3em" className="loader" />}
        </div>
        <div className="chart-cell">
           <GenericChart
              syncId="syncMlp"
              loading={referralsLoading}
              title="Referrals Volume"
              data={referralsData}
              yaxisDataKey="volume"
              rightYaxisDataKey="volumeCumulative"
              yaxisTickFormatter={yaxisFormatterNumber}
              tooltipFormatter={tooltipFormatterNumber}
              tooltipLabelFormatter={tooltipLabelFormatterUnits}
              items={[
                { key: 'volume', name: 'Daily', unit: '$' },
                {
                  key: 'volumeCumulative',
                  name: 'Cumulative',
                  type: 'Line',
                  yAxisId: 'right',
                  strokeWidth: 2,
                  color: COLORS[4],
                  unit: '$'
                }
              ]}
              type="Composed"
              description="Volume generated by registered referrals"
            />
        </div>
        <div className="chart-cell">
           <GenericChart
              syncId="syncMlp"
              loading={referralsLoading}
              title="Referrals Rebates"
              data={referralsData?.map(item => ({ all: item.totalRebateUsd.toFixed(2), ...item }))}
              yaxisDataKey="totalRebateUsd"
              yaxisTickFormatter={yaxisFormatterNumber}
              tooltipFormatter={tooltipFormatterNumber}
              tooltipLabelFormatter={tooltipLabelFormatterUnits}
              items={[
                { key: 'discountUsd', name: 'Discount', unit: '$' },
                { key: 'referrerRebateUsd', name: 'Rebates', unit: '$' }
              ]}
              type="Bar"
              description="Rebates go to Affiliates, Discount go to Traders"
            />
        </div>
        <div className="chart-cell">
           <GenericChart
              syncId="syncMlp"
              loading={referralsLoading}
              title="Registered Affiliates"
              data={referralsData}
              yaxisDataKey="referrersCount"
              rightYaxisDataKey="referrersCountCumulative"
              yaxisTickFormatter={yaxisFormatterNumber}
              tooltipFormatter={tooltipFormatterNumber}
              tooltipLabelFormatter={tooltipLabelFormatterUnits}
              items={[
                { key: 'referrersCount', type: 'Bar', name: 'Daily'},
                // { key: 'referralCodesCount', type: 'Bar', name: 'Codes' },
                {
                  key: 'referrersCountCumulative',
                  strokeWidth: 2,
                  yAxisId: 'right',
                  type: 'Line',
                  name: 'Cumulative Affiliates',
                  color: COLORS[4]
                },
                // {
                //   key: 'referralCodesCountCumulative',
                //   strokeWidth: 2,
                //   yAxisId: 'right',
                //   type: 'Line',
                //   name: 'Cumulative Codes',
                //   color: COLORS[5]
                // }
              ]}
              type="Composed"
            />
        </div>
        <div className="chart-cell">
           <GenericChart
              syncId="syncMlp"
              loading={referralsLoading}
              title="Referrals Registrations"
              data={referralsData}
              yaxisDataKey="referralsCount"
              rightYaxisDataKey="referralsCountCumulative"
              yaxisTickFormatter={yaxisFormatterNumber}
              tooltipFormatter={tooltipFormatterNumber}
              tooltipLabelFormatter={tooltipLabelFormatterUnits}
              items={[
                { key: 'referralsCount', type: 'Bar', name: 'Daily'},
                // { key: 'referralCodesCount', type: 'Bar', name: 'Codes' },
                {
                  key: 'referralsCountCumulative',
                  strokeWidth: 2,
                  yAxisId: 'right',
                  type: 'Line',
                  name: 'Cumulative Referrals',
                  color: COLORS[4]
                },
                // {
                //   key: 'referralCodesCountCumulative',
                //   strokeWidth: 2,
                //   yAxisId: 'right',
                //   type: 'Line',
                //   name: 'Cumulative Codes',
                //   color: COLORS[5]
                // }
              ]}
              type="Composed"
              description="Traders registrations with referral code"
            />
        </div>
      </div>
    </div>
  );
}

export default Referrals;
